import numeral from 'numeral';
// utils
import { localStorageGetItem } from './storage-available';
// locales
import { allLangs, defaultLang } from '../locales';

// ----------------------------------------------------------------------

type InputValue = string | number | null;

// Platform is EUR-only. If multi-currency is ever introduced, switch the
// glyph at this single source and add an FX-conversion layer — never
// swap symbols without converting values.
export const CURRENCY_SYMBOL = '€';

export function fNumber(number: InputValue) {
  return numeral(number).format();
}

export function fCurrencySymbol(year?: number | string, currentLang?: string): string {
  const yearNumber = Number(year);
  return year ? `${CURRENCY_SYMBOL} ${yearNumber}` : `${CURRENCY_SYMBOL} `;
}

export function fCurrency(number: InputValue, currentLang?: string) {
  const lang = currentLang || getCurrentLang();
  const isCurrentLangEn = lang === 'en';

  // only make null and undefined as falsy values
  const formattedNumber =
    number !== null && number !== undefined
      ? `${CURRENCY_SYMBOL} ${`${new Intl.NumberFormat(isCurrentLangEn ? 'en-US' : 'de-DE', {
          style: 'currency',
          currency: 'EUR',
        }).format(Number(number))}`.replace(/€/g, '')}`
      : '';
  const replaceValue = isCurrentLangEn ? '.00' : ',00';
  return result(formattedNumber, replaceValue, lang);
}

export function fCurrencyFormat(number: InputValue, currentLang?: string) {
  const lang = currentLang || getCurrentLang();
  const isCurrentLangEn = lang === 'en';
  const formattedNumber = number
    ? `${`${new Intl.NumberFormat(isCurrentLangEn ? 'en-US' : 'de-DE', {
        style: 'currency',
        currency: 'EUR',
      }).format(Number(number))}`.replace(/€/g, '')}`
    : '';
  const replaceValue = isCurrentLangEn ? '.00' : ',00';
  return result(formattedNumber, replaceValue, lang);
}

export function fCurrencyAfterSymbol(number: InputValue, currentLang?: string) {
  const lang = currentLang || getCurrentLang();
  const isCurrentLangEn = lang === 'en';

  const format = number
    ? `${`${new Intl.NumberFormat(isCurrentLangEn ? 'en-US' : 'de-DE', {
        style: 'currency',
        currency: 'EUR',
      }).format(Number(number))}`.replace(/€/g, '')}${CURRENCY_SYMBOL} `
    : '';

  return result(format, '.00', lang);
}

export function fPercent(number: InputValue) {
  const format = number ? numeral(Number(number) / 100).format('0.00%') : '';

  // This Implementation for zero value
  // Issue was if i pass the zero the out put in column empty and it looks weird, so have added condition for zero
  if (number === 0) {
    const zeroFormat = numeral(number / 100).format('0.00%');

    return result(zeroFormat, '.00', getCurrentLang());
  }

  return result(format, '.00', getCurrentLang());
}

export function fShortenNumber(number: InputValue) {
  const format = number ? numeral(number).format('0.00a') : '';

  return result(format, '.00', getCurrentLang());
}

export function fData(number: InputValue) {
  const format = number ? numeral(number).format('0.0 b') : '';

  return result(format, '.0', getCurrentLang());
}

function result(format: string, key = '.00', currentLang?: string) {
  const isInteger = format.includes(key);
  return isInteger && currentLang !== 'it' ? format.replace(key, '') : format;
}

export function fTotalTableResult({
  tableLength = 0,
  totalPages = 0,
  rowsPerPage = 0,
}: {
  tableLength: number;
  totalPages: number;
  rowsPerPage: number;
}) {
  return totalPages * rowsPerPage > 10 ? totalPages * rowsPerPage : tableLength;
}

// ----------------------------------------------------------------------

/**
 * Get the current selected locale from localStorage
 */
export function getCurrentLang(): string {
  const langStorage = localStorageGetItem('i18nextLng');
  const currentLang = allLangs.find((lang) => lang.value === langStorage) || defaultLang;
  return currentLang.value;
}
